<script lang="ts" setup>
import {endpoints} from "~/constants/endpoints";
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'

interface FaqGroup {
    documentId: string;
    title: string;
    subtitle?: string | null;
	slug: string | null;
    icon: {
		documentId: string;
		url: string;
		width: number;
		height: number;
    } | null;
    articles: {
		documentId: string;
		short_description: string | null;
		title: string;
		text: string | null;
		slug: string | null;
    }[];
}

definePageMeta({
    name: 'faq'
})

const userStore = useUserStore()
const config = useRuntimeConfig()

useHead(
    {
        title: 'Help & Support - YourService.ae',
        meta: [
            {
                name: 'description',
                content: 'Need assistance? Visit our Help & Support page for answers to your questions and get the help you need to maximize your experience on YourService.ae.'
            },
            {
                property: 'og:title',
                content: 'Help & Support - YourService.ae'
            },
            {
                property: 'og:description',
                content: 'Need assistance? Visit our Help & Support page for answers to your questions and get the help you need to maximize your experience on YourService.ae.'
            }
        ]
    }
)

const selectedTab = ref('');

const {data: basesList} = await useAsyncData<FaqGroup[]>('faq_groups',
    () => fetchData(endpoints.faq.groups, {
        method: 'GET',
        query: {
          'populate[articles]': true,
          'populate[icon]': true,
        }
    })
        .then(({data}) => data as FaqGroup[])
        .catch(error => {
            console.log(error.data);
            return [] as FaqGroup[];
        })
);

watch(
    () => basesList.value,
    (newVal) => {
        if (newVal?.length) {
            selectedTab.value = newVal[0].documentId;
        }
    },
    { immediate: true }
);

</script>

<template>
    <div class="container mx-auto px-4 md:my-10">
        <h1 class="text-5xl font-bold text-center max-w-[425px] mx-auto py-10">{{ $t('Hi {username}, how can we help?', {username: userStore.isAuthenticated && userStore.user?.username ? userStore.user?.username : $t('User')}) }}</h1>
        <div class="w-fit mx-auto grid grid-cols-2 bg-gray-200 rounded-full">
          <Button variant="primary" size="lg" class="rounded-full text-md" as-child>
            <NuxtLink :to="{name: 'faq'}">
              {{ $t("Answers") }}
            </NuxtLink>
          </Button>
          <Button variant="link" size="lg" class="rounded-full text-md" as-child>
            <NuxtLink :to="{name: 'faq_ask'}">
              {{ $t("Ask") }}
            </NuxtLink>
          </Button>
        </div>
        
        <div class="mt-12">
            <Tabs :default-value="basesList?.[0]?.documentId" v-model="selectedTab">
                <TabsList class="flex-wrap lg:flex-nowrap flex gap-4 lg:gap-6 w-fit mx-auto lg:border-b lg:border-gray-200">
                    <TabsTrigger v-for="item in basesList" :key="item.documentId" :value="item.documentId" class="text-md lg:rounded-none rounded-lg lg:px-0 bg-gray-100 lg:bg-transparent lg:border-b-2 border-transparent mb-[-1px]" :class="{'!bg-primary-100 lg:!bg-transparent lg:border-primary-500': selectedTab === item.documentId}">
                        {{ item.title }}
                    </TabsTrigger>
                </TabsList>
                <TabsContent v-for="item in basesList" :key="item.documentId" :value="item.documentId" class="mt-6">
					<div class="grid lg:grid-cols-3 gap-6">
						<NuxtLink 
							:to="{name: 'faq_article', params: {article: article.slug ||article.documentId, id: item.slug || item.documentId}}" 
							v-for="article in item.articles" 
							:key="article.documentId" 
							class="p-6 border border-gray-100 rounded-xl flex items-center gap-6"
						>
							<div class="rounded-full bg-gray-100 flex items-center justify-center w-16 h-16 flex-shrink-0">
								<NuxtImg :src="item.icon?.url" class="max-h-10" />
							</div>
							<div class="grow">
								<h2 class="text-md font-bold">{{ article.title }}</h2>
								<div v-if="article?.short_description" class="text-sm text-zinc-600 mt-2">{{ article?.short_description }}</div>
							</div>
						</NuxtLink>
					</div>
                </TabsContent>
            </Tabs>
        </div>
    </div>

</template>


<style lang="scss" scoped>

.icon-container {
  width: 64px;
  height: 64px;
  padding: 16px;
  gap: 16px;
  border-radius: 96px;
  background: rgba(246, 248, 250, 1);
}

.custom_style_adaptive {
  @media(max-width: 768px) {
    min-height: auto !important;
  }
}

.ys-support-theme-box {
  padding: 16px 32px 16px 32px;
  gap: 30px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(226, 228, 233, 1);

}


.ys-home-card {
  border-radius: 36px;
  background: rgba(247, 254, 248, 1);
  height: 268px;
  padding: 32px;
  gap: 30px;
}

.ys-help-title-text {
  font-family: Involve, sans-serif;
  color: rgba(0, 0, 0, 1);
  font-size: 34px;
  font-weight: 400;
  line-height: 45px;
  text-align: center;
  @media(max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
  @media(max-width: 420px) {
    font-size: 18px;
    line-height: 24px;
  }
}

.ys-help-custom-title-text {
  font-family: Involve, sans-serif;
  font-style: italic;
  color: rgba(58, 62, 89, 1);
  font-size: 34px;
  font-weight: 500;
  line-height: 45px;
  text-align: left;
  @media(max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
  @media(max-width: 420px) {
    font-size: 18px;
    line-height: 24px;
  }
}


.ys-text {
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;
  color: rgba(0, 0, 0, 1);
}

.ys-text-custom {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: rgba(0, 0, 0, 1);
}

</style>